import React, { useState, useEffect } from "react";
import { Check, X, ChevronLeft, ChevronRight, RotateCcw, RotateCw } from "lucide-react";
import { heartModules } from "../data/modules/heart/heartModules";
import { brainModules } from "../data/modules/brain/brainModules";
import { lungsModules } from "../data/modules/lungs/lungsModules";
import { colonModules } from "../data/modules/colon/colonModules";
import { liverModules } from "../data/modules/liver/liverModules";
import { kidneyModules } from "../data/modules/kidney/kidneyModules";
import { womenreproductiveModules } from "../data/modules/womenreproductive/womenreproductiveModules";
import { thyroidModules } from "../data/modules/thyroid/thyroidModules";
import { skeletonModules } from "../data/modules/skeleton/skeletonModules";
import { adrenalglandModules } from "../data/modules/adrenalglands/adrenalglandModules";
import { usmle1Modules } from "../data/modules/usmle1/usmle1Modules";
import { usmle2Modules } from "../data/modules/usmle2/usmle2Modules";
import { geneticsModules } from "../data/modules/genetics/geneticsModules";
import { geneticsMethodsModules } from "../data/modules/geneticsMethods/geneticsMethodsModules";
import { trackModuleComplete } from "../lib/analytics";

const OrganModule = ({ organ, userData, saveUserData, onComplete, onBack, initialModuleIndex = 0, forceRestart = false }) => {
  const [currentModule, setCurrentModule] = useState(initialModuleIndex);
  const [currentSection, setCurrentSection] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [sectionAnswers, setSectionAnswers] = useState({});
  const [sectionResults, setSectionResults] = useState({});
  const [isFlashcardFlipped, setIsFlashcardFlipped] = useState(false);
  const [orderingItems, setOrderingItems] = useState([]);
  const [orderingSubmitted, setOrderingSubmitted] = useState(false);
  const [orderingCorrect, setOrderingCorrect] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Get the correct modules based on organ
  let modules;
  if (organ === "heart") modules = heartModules;
  else if (organ === "brain") modules = brainModules;
  else if (organ === "lungs") modules = lungsModules;
  else if (organ === "colon") modules = colonModules;
  else if (organ === "liver") modules = liverModules;
  else if (organ === "kidney") modules = kidneyModules;
  else if (organ === "womenreproductive") modules = womenreproductiveModules;
  else if (organ === "thyroid") modules = thyroidModules;
  else if (organ === "skeleton") modules = skeletonModules;
  else if (organ === "adrenalgland") modules = adrenalglandModules;
  else if (organ === "usmle1") modules = usmle1Modules;
  else if (organ === "usmle2") modules = usmle2Modules;
  else if (organ === "genetics") modules = geneticsModules;
  else if (organ === "genetics-methods") modules = geneticsMethodsModules;

  const currentModuleData = modules[currentModule];
  const currentSectionData = currentModuleData?.sections?.[currentSection];
  const totalSections = currentModuleData?.sections?.length || 0;
  const progress = ((currentSection + 1) / totalSections) * 100;

  const organData = userData.organProgress[organ] || {
    moduleData: {},
    completed: [],
  };
  const moduleProgress =
    organData.moduleData && organData.moduleData[currentModuleData.id]
      ? organData.moduleData[currentModuleData.id]
      : null;
  const isFirstTime = !moduleProgress || !moduleProgress.completed;
  const needsReview =
    moduleProgress &&
    moduleProgress.nextReviewDate &&
    new Date() >= new Date(moduleProgress.nextReviewDate);

  useEffect(() => {
    if (!organData.moduleData) {
      setSectionAnswers({});
      setSectionResults({});
      setCurrentSection(0);
      setSelectedAnswer(null);
      setIsCorrect(null);
      return;
    }
    const savedProgress = organData.moduleData[currentModuleData.id];
    if (forceRestart) {
      setSectionAnswers({});
      setSectionResults({});
      setCurrentSection(0);
    } else {
      setSectionAnswers(savedProgress?.sectionAnswers || {});
      setSectionResults(savedProgress?.sectionResults || {});
      const savedSection = Number.isInteger(savedProgress?.currentSection)
        ? savedProgress.currentSection
        : 0;
      setCurrentSection(Math.min(savedSection, totalSections - 1));
    }
    setSelectedAnswer(null);
    setIsCorrect(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentModule, organ]);

  useEffect(() => {
    // Reset flashcard flip state when changing sections
    setIsFlashcardFlipped(false);

    if (currentSectionData.type === "text" || currentSectionData.type === "flashcard") {
      setSelectedAnswer(null);
      setIsCorrect(null);
      return;
    }

    if (currentSectionData.type === "ordering") {
      const hasSavedResult = Object.prototype.hasOwnProperty.call(sectionResults, currentSection);
      if (hasSavedResult) {
        setOrderingItems([...currentSectionData.items]);
        setOrderingSubmitted(true);
        setOrderingCorrect(true);
      } else {
        const shuffled = [...currentSectionData.items].sort(() => Math.random() - 0.5);
        setOrderingItems(shuffled);
        setOrderingSubmitted(false);
        setOrderingCorrect(false);
      }
      return;
    }

    const hasAnswer = Object.prototype.hasOwnProperty.call(
      sectionAnswers,
      currentSection,
    );
    if (hasAnswer) {
      setSelectedAnswer(sectionAnswers[currentSection]);
      setIsCorrect(
        Object.prototype.hasOwnProperty.call(sectionResults, currentSection)
          ? sectionResults[currentSection]
          : null,
      );
    } else {
      setSelectedAnswer(null);
      setIsCorrect(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSection, currentModule, sectionAnswers, sectionResults]);

  const persistModuleProgress = (updates) => {
    const currentOrganProgress = userData.organProgress[organ] || {
      moduleData: {},
      completed: [],
    };
    const currentModData = currentOrganProgress.moduleData || {};
    const existingModuleData = currentModData[currentModuleData.id] || {};
    const newModuleData = {
      ...currentModData,
      [currentModuleData.id]: {
        ...existingModuleData,
        ...updates,
        lastAccessedAt: new Date().toISOString(), // Add timestamp
      },
    };

    const newUserData = {
      ...userData,
      organProgress: {
        ...userData.organProgress,
        [organ]: {
          ...currentOrganProgress,
          moduleData: newModuleData,
        },
      },
    };

    saveUserData(newUserData);
  };

  const getNextReviewDate = (reviewCount) => {
    const intervals = [1, 3, 7, 14, 30];
    const days = intervals[Math.min(reviewCount, intervals.length - 1)];
    const nextDate = new Date();
    nextDate.setDate(nextDate.getDate() + days);
    return nextDate.toISOString();
  };

  const handleNext = () => {
    if (currentSection < totalSections - 1) {
      const nextSection = currentSection + 1;
      setCurrentSection(nextSection);
      setSelectedAnswer(null);
      setIsCorrect(null);
      persistModuleProgress({
        sectionAnswers,
        sectionResults,
        currentSection: nextSection,
      });
    } else {
      // Module completed
      const currentModData = organData.moduleData || {};
      const existingModuleData = currentModData[currentModuleData.id] || {};
      const newModuleData = {
        ...currentModData,
        [currentModuleData.id]: {
          ...existingModuleData,
          completed: true,
          completedDate: new Date().toISOString(),
          reviewCount:
            (moduleProgress && moduleProgress.reviewCount
              ? moduleProgress.reviewCount
              : 0) + 1,
          nextReviewDate: getNextReviewDate(
            (moduleProgress && moduleProgress.reviewCount
              ? moduleProgress.reviewCount
              : 0) + 1,
          ),
        },
      };

      const newAward = isFirstTime
        ? {
            id: `${organ}-${currentModuleData.id}-${Date.now()}`,
            organ,
            moduleId: currentModuleData.id,
            moduleTitle: currentModuleData.title,
            date: new Date().toISOString(),
          }
        : null;
      const updatedOrganCompleted = isFirstTime
        ? [...organData.completed, currentModuleData.id]
        : organData.completed;
      const updatedOrganProgress = {
        ...userData.organProgress,
        [organ]: {
          ...organData,
          moduleData: newModuleData,
          completed: updatedOrganCompleted,
        },
      };

      if (organ === "genetics-methods") {
        const methodsTotal = (modules || []).length;
        if (updatedOrganCompleted.length >= methodsTotal) {
          const geneticsProgress = userData.organProgress.genetics || {
            completed: [],
            moduleData: {},
            currentModule: 0,
            score: 0,
          };
          if (!geneticsProgress.completed.includes("methods")) {
            updatedOrganProgress.genetics = {
              ...geneticsProgress,
              completed: [...geneticsProgress.completed, "methods"],
            };
          }
        }
      }

      const newUserData = {
        ...userData,
        organProgress: updatedOrganProgress,
        awards: newAward
          ? [...(userData.awards || []), newAward]
          : userData.awards || [],
      };

      saveUserData(newUserData);

      // Award XP based on questions answered correctly
      const xpPerQuestion = 25;
      const correctCount = Object.values(sectionResults).filter(Boolean).length;
      const totalXP = correctCount * xpPerQuestion;

      if (isFirstTime) {
        onComplete(totalXP, false);
        // Track module completion in Google Analytics
        trackModuleComplete(organ, currentModule, correctCount, totalXP);
        alert(
          "Module completed! +" +
            totalXP +
            " XP and " +
            Math.floor(totalXP / 10) +
            " coins! 🎉",
        );
      } else if (needsReview) {
        onComplete(totalXP, true);
        alert("Review completed! +" + Math.floor(totalXP / 50) + " coins! 🔄");
      } else {
        alert("Already mastered! Come back later. 📚");
      }

      // Move to next module or go back
      if (currentModule < modules.length - 1) {
        setCurrentModule(currentModule + 1);
        setCurrentSection(0);
        setSectionAnswers({});
        setSectionResults({});
        setSelectedAnswer(null);
        setIsCorrect(null);
      } else {
        onBack();
      }
    }
  };

  const handlePrevious = () => {
    if (currentSection === 0) return;
    const nextSection = currentSection - 1;
    setCurrentSection(nextSection);
    setSelectedAnswer(null);
    setIsCorrect(null);
    persistModuleProgress({
      sectionAnswers,
      sectionResults,
      currentSection: nextSection,
    });
  };

  const handleAnswer = (index) => {
    setSelectedAnswer(index);
    const correct = index === currentSectionData.correct;
    setIsCorrect(correct);
    setSectionAnswers((prev) => {
      const next = { ...prev, [currentSection]: index };
      persistModuleProgress({
        sectionAnswers: next,
        sectionResults: {
          ...sectionResults,
          [currentSection]: correct,
        },
        currentSection,
      });
      return next;
    });
    setSectionResults((prev) => ({ ...prev, [currentSection]: correct }));
  };

  const handleDragStart = (index) => setDraggedIndex(index);

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    const newItems = [...orderingItems];
    const [removed] = newItems.splice(draggedIndex, 1);
    newItems.splice(index, 0, removed);
    setOrderingItems(newItems);
    setDraggedIndex(index);
  };

  const handleDrop = () => setDraggedIndex(null);

  const handleOrderingSubmit = () => {
    const correct = orderingItems.every((item, i) => item === currentSectionData.items[i]);
    setOrderingSubmitted(true);
    setOrderingCorrect(correct);
    if (correct) {
      setSectionResults((prev) => ({ ...prev, [currentSection]: true }));
      persistModuleProgress({
        sectionAnswers,
        sectionResults: { ...sectionResults, [currentSection]: true },
        currentSection,
      });
    }
  };

  const handleOrderingReset = () => {
    const shuffled = [...currentSectionData.items].sort(() => Math.random() - 0.5);
    setOrderingItems(shuffled);
    setOrderingSubmitted(false);
    setOrderingCorrect(false);
  };

  if (!currentModuleData || !currentSectionData) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={onBack}
          className="mb-4 text-teal-600 hover:text-teal-700"
        >
          ← Back to Overview
        </button>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <p className="text-gray-700">Module content is unavailable.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="mb-4 flex justify-between items-center">
        <button
          onClick={onBack}
          className="text-white hover:text-gray-200"
        >
          ← Back to Overview
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrevious}
            disabled={currentSection === 0}
            className={
              "p-2 rounded-lg font-semibold transition-colors flex items-center justify-center " +
              (currentSection === 0
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-teal-500 text-white hover:bg-teal-600")
            }
            aria-label="Previous section"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={handleNext}
            disabled={
              (currentSectionData.type === "question" && selectedAnswer === null) ||
              (currentSectionData.type === "ordering" && (!orderingSubmitted || !orderingCorrect))
            }
            className={
              "p-2 rounded-lg font-semibold transition-colors flex items-center justify-center " +
              ((currentSectionData.type === "question" && selectedAnswer === null) ||
               (currentSectionData.type === "ordering" && (!orderingSubmitted || !orderingCorrect))
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-teal-500 text-white hover:bg-teal-600")
            }
            aria-label="Next section"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 capitalize">
          {organ} Module: {currentModuleData.title}
        </h2>
        <div className="relative">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-gray-600">
              Section {currentSection + 1} of {totalSections}
            </span>
            <span className="text-sm font-medium text-teal-600">
              {Math.round(progress)}% Complete
            </span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-teal-500 transition-all duration-500"
              style={{ width: progress + "%" }}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
        {currentSectionData.type === "text" ? (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-bold text-gray-800">
                {currentModuleData.title}
              </h3>
              {!isFirstTime && !needsReview && (
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                  ✓ Completed
                </span>
              )}
              {needsReview && (
                <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
                  📚 Due for Review
                </span>
              )}
            </div>

            <div
              className="text-gray-700 leading-relaxed mb-6 text-lg prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: currentSectionData.content }}
            />

            {currentSectionData.image && (
              <div className="mb-6">
                <img
                  src={currentSectionData.image}
                  alt={currentModuleData.title}
                  className="w-full rounded-lg shadow-md"
                />
              </div>
            )}

            {currentSectionData.video && (
              <div className="mb-6">
                <video
                  src={currentSectionData.video}
                  controls
                  className="w-full rounded-lg shadow-md"
                />
              </div>
            )}

            {currentSectionData.funFact && (
              <div className="bg-teal-50 border-l-4 border-teal-400 p-4 mb-6 rounded">
                <p className="text-sm text-gray-700">
                  {currentSectionData.funFact}
                </p>
              </div>
            )}

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
              <button
                onClick={handlePrevious}
                disabled={currentSection === 0}
                className={
                  "w-full sm:w-auto px-5 py-3 rounded-lg font-semibold border transition-colors " +
                  (currentSection === 0
                    ? "border-gray-200 text-gray-400 cursor-not-allowed"
                    : "border-gray-300 text-gray-700 hover:border-teal-400 hover:text-teal-700")
                }
              >
                Previous
              </button>
              <button
                onClick={handleNext}
                className="w-full sm:w-auto bg-teal-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-600 transition-colors"
              >
                {currentSection < totalSections - 1
                  ? "Continue"
                  : "Complete Module"}
              </button>
            </div>
          </>
        ) : currentSectionData.type === "flashcard" ? (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800">
                Flashcard
              </h3>
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                Click to flip
              </span>
            </div>

            <div
              className="relative w-full cursor-pointer perspective-1000"
              style={{ minHeight: "400px" }}
              onClick={() => setIsFlashcardFlipped(!isFlashcardFlipped)}
            >
              <div
                className={
                  "relative w-full h-full transition-transform duration-500 transform-style-preserve-3d " +
                  (isFlashcardFlipped ? "rotate-y-180" : "")
                }
                style={{
                  transformStyle: "preserve-3d",
                  transform: isFlashcardFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
                }}
              >
                {/* Front of card */}
                <div
                  className="absolute w-full bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl p-8 shadow-lg flex flex-col items-center justify-center text-center border border-purple-300"
                  style={{
                    minHeight: "400px",
                    backfaceVisibility: "hidden",
                  }}
                >
                  <div
                    className="text-purple-900 text-xl font-bold mb-4 flashcard-content"
                    dangerouslySetInnerHTML={{ __html: currentSectionData.front }}
                  />
                  {currentSectionData.frontImage && (
                    <img
                      src={currentSectionData.frontImage}
                      alt="Flashcard front"
                      className="max-w-full max-h-64 rounded-lg shadow-md"
                    />
                  )}
                  <div className="mt-4 flex items-center gap-2 text-purple-600">
                    <RotateCw className="w-5 h-5" />
                    <span className="text-sm">Click to reveal answer</span>
                  </div>
                </div>

                {/* Back of card */}
                <div
                  className="absolute w-full bg-gradient-to-br from-teal-100 to-teal-200 rounded-xl p-8 shadow-lg flex flex-col items-center justify-start text-center overflow-y-auto border border-teal-300"
                  style={{
                    minHeight: "400px",
                    maxHeight: "400px",
                    backfaceVisibility: "hidden",
                    transform: "rotateY(180deg)",
                  }}
                >
                  <div
                    className="text-teal-900 text-lg mb-4 flashcard-content prose prose-li:text-teal-900 prose-ul:text-left prose-ol:text-left w-full"
                    dangerouslySetInnerHTML={{ __html: currentSectionData.back }}
                  />
                  {currentSectionData.backImage && (
                    <img
                      src={currentSectionData.backImage}
                      alt="Flashcard back"
                      className="max-w-full max-h-64 rounded-lg shadow-md"
                    />
                  )}
                  <div className="mt-4 flex items-center gap-2 text-teal-600">
                    <RotateCw className="w-5 h-5" />
                    <span className="text-sm">Click to flip back</span>
                  </div>
                </div>
              </div>
            </div>

            {currentSectionData.hint && !isFlashcardFlipped && (
              <div className="mt-4 bg-purple-50 border-l-4 border-purple-400 p-4 rounded">
                <p className="text-sm text-gray-700">
                  <strong>Hint:</strong> {currentSectionData.hint}
                </p>
              </div>
            )}

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-between">
              <button
                onClick={handlePrevious}
                disabled={currentSection === 0}
                className={
                  "w-full sm:w-auto px-5 py-3 rounded-lg font-semibold border transition-colors " +
                  (currentSection === 0
                    ? "border-gray-200 text-gray-400 cursor-not-allowed"
                    : "border-gray-300 text-gray-700 hover:border-teal-400 hover:text-teal-700")
                }
              >
                Previous
              </button>
              <button
                onClick={handleNext}
                className="w-full sm:w-auto bg-teal-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-600 transition-colors"
              >
                {currentSection < totalSections - 1
                  ? "Continue"
                  : "Complete Module"}
              </button>
            </div>
          </>
        ) : currentSectionData.type === "video" ? (
          <>
            {currentSectionData.title && (
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                {currentSectionData.title}
              </h3>
            )}

            <video
              src={currentSectionData.src}
              controls
              className="w-full rounded-lg shadow-md mb-6"
            />

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
              <button
                onClick={handlePrevious}
                disabled={currentSection === 0}
                className={
                  "w-full sm:w-auto px-5 py-3 rounded-lg font-semibold border transition-colors " +
                  (currentSection === 0
                    ? "border-gray-200 text-gray-400 cursor-not-allowed"
                    : "border-gray-300 text-gray-700 hover:border-teal-400 hover:text-teal-700")
                }
              >
                Previous
              </button>
              <button
                onClick={handleNext}
                className="w-full sm:w-auto bg-teal-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-600 transition-colors"
              >
                {currentSection < totalSections - 1
                  ? "Continue"
                  : "Complete Module"}
              </button>
            </div>
          </>
        ) : currentSectionData.type === "ordering" ? (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800">Ordering Challenge</h3>
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                Drag to reorder
              </span>
            </div>

            <p className="text-gray-700 mb-6 text-lg">{currentSectionData.question}</p>

            <div className="space-y-2 mb-6">
              {orderingItems.map((item, index) => (
                <div
                  key={item}
                  draggable={!orderingSubmitted}
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={handleDrop}
                  className={
                    "flex items-center gap-3 p-4 rounded-lg border-2 select-none transition-all " +
                    (orderingSubmitted
                      ? item === currentSectionData.items[index]
                        ? "border-green-500 bg-green-50 cursor-default"
                        : "border-red-400 bg-red-50 cursor-default"
                      : draggedIndex === index
                        ? "border-teal-400 bg-teal-50 opacity-60 cursor-grabbing"
                        : "border-gray-300 bg-white hover:border-teal-400 cursor-grab")
                  }
                >
                  <span className="w-7 h-7 rounded-full bg-teal-100 text-teal-700 font-bold text-sm flex items-center justify-center flex-shrink-0">
                    {index + 1}
                  </span>
                  <span className="flex-1 font-medium text-gray-800">{item}</span>
                  {!orderingSubmitted && (
                    <span className="text-gray-400 text-lg">≡</span>
                  )}
                  {orderingSubmitted && (
                    item === currentSectionData.items[index]
                      ? <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                      : <X className="w-5 h-5 text-red-500 flex-shrink-0" />
                  )}
                </div>
              ))}
            </div>

            {!orderingSubmitted && (
              <button
                onClick={handleOrderingSubmit}
                className="w-full bg-blue-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors mb-4"
              >
                Check Order
              </button>
            )}

            {orderingSubmitted && (
              <div
                className={
                  "p-4 rounded-lg border-2 mb-4 " +
                  (orderingCorrect ? "bg-green-50 border-green-500" : "bg-red-50 border-red-400")
                }
              >
                <div className="flex items-start gap-3">
                  {orderingCorrect
                    ? <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    : <X className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  }
                  <div>
                    <h4 className={"font-bold text-lg mb-2 " + (orderingCorrect ? "text-green-800" : "text-red-800")}>
                      {orderingCorrect ? "Perfect order! 🎉" : "Not quite right"}
                    </h4>
                    {currentSectionData.explanation && (
                      <p className="text-gray-700">{currentSectionData.explanation}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {orderingSubmitted && !orderingCorrect && (
              <button
                onClick={handleOrderingReset}
                className="mb-4 flex items-center gap-2 text-teal-600 hover:text-teal-700"
              >
                <RotateCcw className="w-4 h-4" />
                Try again
              </button>
            )}

            <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:justify-between">
              <button
                onClick={handlePrevious}
                disabled={currentSection === 0}
                className={
                  "w-full sm:w-auto px-5 py-3 rounded-lg font-semibold border transition-colors " +
                  (currentSection === 0
                    ? "border-gray-200 text-gray-400 cursor-not-allowed"
                    : "border-gray-300 text-gray-700 hover:border-teal-400 hover:text-teal-700")
                }
              >
                Previous
              </button>
              {orderingCorrect && (
                <button
                  onClick={handleNext}
                  className="w-full sm:w-auto bg-teal-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-600 transition-colors"
                >
                  {currentSection < totalSections - 1 ? "Continue" : "Complete Module"}
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <h3 className="text-xl font-bold text-gray-800 mb-6">
              {currentSectionData.text}
            </h3>

            {/* Question Image (optional - shown with question) */}
            {currentSectionData.image && (
              <div className="mb-6 flex justify-center">
                <img
                  src={currentSectionData.image}
                  alt="Question"
                  className="max-w-full rounded-lg shadow-md border-2 border-gray-200"
                  style={{ maxHeight: "400px" }}
                />
              </div>
            )}

            <div className="space-y-3">
              {(currentSectionData.options || []).map((option, idx) => {
                const isCorrectOption = idx === currentSectionData.correct;

                return (
                  <button
                    key={idx}
                    onClick={() => selectedAnswer === null && handleAnswer(idx)}
                    disabled={selectedAnswer !== null}
                    className={
                      "w-full p-4 rounded-lg border-2 text-left transition-all " +
                      (selectedAnswer === null
                        ? "border-gray-300 hover:border-teal-400 hover:bg-teal-50"
                        : isCorrectOption
                          ? "border-green-500 bg-green-50"
                          : idx === selectedAnswer
                            ? "border-red-500 bg-red-50"
                            : "border-gray-200 opacity-50")
                    }
                  >
                    <div className="flex items-center justify-between">
                      <span>{option}</span>
                      {selectedAnswer !== null && isCorrectOption && (
                        <Check className="w-5 h-5 text-green-600" />
                      )}
                      {selectedAnswer === idx && !isCorrect && (
                        <X className="w-5 h-5 text-red-600" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Show explanation and image when answered */}
            {selectedAnswer !== null && (
              <div
                className={
                  "mt-6 p-4 rounded-lg border-2 " +
                  (isCorrect
                    ? "bg-green-50 border-green-500"
                    : "bg-red-50 border-red-500")
                }
              >
                <div className="flex items-start gap-3 mb-3">
                  {isCorrect ? (
                    <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  ) : (
                    <X className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  )}
                  <div className="flex-1">
                    <h4
                      className={
                        "font-bold text-lg mb-2 " +
                        (isCorrect ? "text-green-800" : "text-red-800")
                      }
                    >
                      {isCorrect ? "Correct! 🎉" : "Not quite right"}
                    </h4>
                    {currentSectionData.explanation && (
                      <p className="text-gray-700 mb-3">
                        {currentSectionData.explanation}
                      </p>
                    )}
                  </div>
                </div>

                {/* Show explanatory image when available */}
                {currentSectionData.explanationImage && (
                  <div className="mt-4 bg-white p-3 rounded-lg border border-gray-200">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      📚 Here's a visual explanation:
                    </p>
                    <img
                      src={currentSectionData.explanationImage}
                      alt="Explanation"
                      className="w-full rounded-lg shadow-md"
                    />
                  </div>
                )}
              </div>
            )}

            {selectedAnswer !== null && !isCorrect && (
              <button
                onClick={() => {
                  setSelectedAnswer(null);
                  setIsCorrect(null);
                  setSectionAnswers((prev) => {
                    const next = { ...prev };
                    delete next[currentSection];
                    persistModuleProgress({
                      sectionAnswers: next,
                      sectionResults: Object.keys(sectionResults).reduce(
                        (acc, key) => {
                          if (Number(key) !== currentSection) {
                            acc[key] = sectionResults[key];
                          }
                          return acc;
                        },
                        {},
                      ),
                      currentSection,
                    });
                    return next;
                  });
                  setSectionResults((prev) => {
                    const next = { ...prev };
                    delete next[currentSection];
                    return next;
                  });
                }}
                className="mt-4 flex items-center gap-2 text-teal-600 hover:text-teal-700"
              >
                <RotateCcw className="w-4 h-4" />
                Try again
              </button>
            )}

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-between">
              <button
                onClick={handlePrevious}
                disabled={currentSection === 0}
                className={
                  "w-full sm:w-auto px-5 py-3 rounded-lg font-semibold border transition-colors " +
                  (currentSection === 0
                    ? "border-gray-200 text-gray-400 cursor-not-allowed"
                    : "border-gray-300 text-gray-700 hover:border-teal-400 hover:text-teal-700")
                }
              >
                Previous
              </button>
              {isCorrect && (
                <button
                  onClick={handleNext}
                  className="w-full sm:w-auto bg-teal-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-600 transition-colors"
                >
                  {currentSection < totalSections - 1
                    ? "Continue"
                    : "Complete Module"}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrganModule;
